import { InputOutputSelector } from '@modules/input-output-devices/InputOutputSelector';
import { AudioNoiseFilterPopover } from '@modules/input-output-devices/InputOutputSelector/AudioNoiseFilter';
import { Collapse, IconButton, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Stack } from '@mui/system';
import { ConnectionQuality } from 'livekit-client';
import { useNavigate } from 'react-router';
import { IChatConversation } from 'src/@types/chat';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { useAuthContext } from 'src/auth/useAuthContext';
import { PopoverTrigger } from 'src/components/PopoverTrigger';
import { CustomAvatar } from 'src/components/custom-avatar';
import Iconify from 'src/components/iconify';
import { useLocales } from 'src/locales';
import { useVoiceChannelContext } from 'src/provider/VoiceChannel';
import { ConnectionStatusIcon } from './ConnectionStatusIcon';
import { ParticipantsCount } from './ParticipantsCount';
import { ConnectionLoadingContainer, CustomChannelTitle } from './styles';

const HeaderContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  '& .expanded-nav-bar': {
    display: 'grid',
  },
}));

type Props = {
  isConnected: boolean;
  channel: IChatConversation;
  connectionStatus: string;
  connectionQuality?: number | ConnectionQuality;
  participantsCount: number;
  raiseHand?: () => void;
  hand?: boolean;
  children: React.ReactNode;
};

export function RTCBar({
  isConnected,
  channel,
  connectionStatus,
  connectionQuality,
  participantsCount,
  children,
}: Props) {
  const { user } = useAuthContext();

  const navigate = useNavigate();
  const { translate } = useLocales();
  const { disconnectVoiceChannel } = useVoiceChannelContext();
  if (!isConnected) return null;

  const handleNavigateToChannel = () => {
    navigate(PATH_DASHBOARD.chat.view(channel._id));
  };

  return (
    <HeaderContainer>
      <Stack direction="column" p={1}>
        <Collapse in={isConnected} orientation="vertical">
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              alignItems="center"
              gap={1}
              justifyContent="space-between"
              width="100%"
            >
              <Stack direction="row" alignItems="center" gap={0.5} width="100%">
                <ConnectionStatusIcon
                  connectionStatus={connectionStatus}
                  connectionQuality={connectionQuality || 0}
                />
                {connectionStatus === 'connected' && (
                  <CustomChannelTitle
                    variant="subtitle2"
                    onClick={handleNavigateToChannel}
                    color="success.main"
                  >
                    {channel?.name}
                  </CustomChannelTitle>
                )}
              </Stack>

              {connectionStatus === 'connected' && (
                <Stack direction="row" alignItems="center">
                  <ParticipantsCount count={participantsCount} />
                  <AudioNoiseFilterPopover />
                  <Tooltip
                    placement="top"
                    arrow
                    title={translate('communicate.rtc_controls.hang_up')}
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => disconnectVoiceChannel()}
                      >
                        <Iconify
                          icon="tabler:phone-x"
                          color={(theme) => theme.palette.error.main}
                        />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Collapse>

        <ConnectionLoadingContainer
          sx={{ ...(connectionStatus !== 'connected' && { mt: 2 }) }}
        >
          {connectionStatus === 'connected' && (
            <Stack
              direction="column"
              alignItems="center"
              gap={0.5}
              width="100%"
            >
              {children}
            </Stack>
          )}

          {user && connectionStatus !== 'connected' && (
            <>
              <CustomAvatar
                name={channel?.name}
                src={channel?.picture}
                variant="rounded"
              />

              <Stack
                direction="column"
                alignItems="flex-start"
                flexGrow={1}
                overflow="hidden"
                maxWidth={1}
              >
                <Typography
                  variant="subtitle2"
                  noWrap
                  textOverflow="ellipsis"
                  textTransform="capitalize"
                  maxWidth={1}
                >
                  {channel?.name}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  textOverflow="ellipsis"
                  textTransform="capitalize"
                  maxWidth={1}
                >
                  {user?.name}
                </Typography>
              </Stack>
            </>
          )}

          {connectionStatus !== 'connected' && (
            <PopoverTrigger popover={InputOutputSelector}>
              <Tooltip
                placement="top"
                arrow
                title={translate('communicate.rtc_controls.device_settings')}
              >
                <span>
                  <IconButton
                    size="small"
                    disabled={connectionStatus !== 'connected'}
                  >
                    <Iconify icon="ic:round-settings" />
                  </IconButton>
                </span>
              </Tooltip>
            </PopoverTrigger>
          )}
        </ConnectionLoadingContainer>
      </Stack>
    </HeaderContainer>
  );
}
